import React, { useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DailyActivity, fetchSystemKills } from '../../api/killboard';
import KillRow from './KillRow';
import ConsolePanel from '../../components/ConsolePanel';
import LcarsRule from '../../components/LcarsRule';
import StatusBadge from '../../components/StatusBadge';
import { C, FONT, frameSx, hexToRgba, lcarsLabelSx, prefersReducedMotion, thinScrollSx } from '../../theme/tokens';
import { useSystemIntel } from './SystemPage';

const DAY_OPTIONS: (7 | 14 | 30)[] = [7, 14, 30];
type SortKey = 'time' | 'value';

/** The full killboard view: when this hole is actually busy, who is busy in
 * it, and what died recently. The per-day timeline is the point — a flat
 * "12 kills in 7 days" says nothing about whether they all landed in one
 * evening ping three weeks ago. */
const SystemKillboard: React.FC = () => {
  const { system } = useSystemIntel();
  const [days, setDays] = useState<7 | 14 | 30>(30);
  const [sort, setSort] = useState<SortKey>('time');

  const kills = useQuery({
    queryKey: ['killboard', system.solar_system_id, days],
    queryFn: () => fetchSystemKills(system.solar_system_id, days),
  });

  const sortedKills = useMemo(() => {
    const list = kills.data?.kills ?? [];
    return [...list].sort((a, b) =>
      sort === 'value' ? b.totalValue - a.totalValue : new Date(b.killmail_time).getTime() - new Date(a.killmail_time).getTime(),
    );
  }, [kills.data, sort]);

  return (
    <Stack spacing={2}>
      <ConsolePanel
        title="Activity"
        accent={C.cyan}
        corners={false}
        sx={frameSx(C.cyan)}
        action={
          <Stack direction="row" spacing={0.5}>
            {DAY_OPTIONS.map((d) => (
              <Chip
                key={d}
                label={`${d}d`}
                size="small"
                onClick={() => setDays(d)}
                sx={{
                  bgcolor: 'transparent',
                  color: days === d ? C.cyan : C.textDim,
                  border: `1px solid ${days === d ? C.cyan : C.borderSub}`,
                  fontFamily: FONT.mono,
                  cursor: 'pointer',
                }}
              />
            ))}
          </Stack>
        }
      >
        {kills.isLoading ? (
          <CircularProgress size={20} sx={{ color: C.cyan }} />
        ) : kills.isError ? (
          <Typography sx={{ color: C.textDim }}>Killboard unavailable right now.</Typography>
        ) : !kills.data || kills.data.stats.totalKills === 0 ? (
          <Typography sx={{ color: C.textDim }}>No kills recorded here in the last {days} days.</Typography>
        ) : (
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ color: C.text, fontSize: '0.9rem' }}>
                {kills.data.stats.truncated ? 'at least ' : ''}
                {kills.data.stats.totalKills} kills in the last {kills.data.stats.windowDays} days
              </Typography>
              {kills.data.stats.truncated && (
                <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>
                  Too busy to page fully — zKillboard's feed was capped before the window start.
                </Typography>
              )}
              {kills.data.stats.mostRecentKill && (
                <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>
                  Most recent {kills.data.stats.mostRecentKill.daysAgo.toFixed(1)} days ago
                </Typography>
              )}
            </Box>
            <LcarsRule accent={C.cyan} />
            <DayActivityChart days={kills.data.stats.dailyActivity} windowDays={days} />
          </Stack>
        )}
      </ConsolePanel>

      {kills.data && kills.data.stats.dailyActivity.length > 0 && (
        <ConsolePanel title="Kill Calendar" accent={C.red} corners={false} sx={frameSx(C.red)}>
          <KillCalendarSection days={kills.data.stats.dailyActivity} />
        </ConsolePanel>
      )}

      {kills.data && (kills.data.activeAlliances.length > 0 || kills.data.activeCorps.length > 0) && (
        <ConsolePanel title="Most Active Here" accent={C.blue} corners={false} sx={frameSx(C.blue)}>
          <Typography sx={{ color: C.textDim, fontSize: '0.8rem', mb: 1.25 }}>
            Ranked by appearances on kills in this window, as victim or attacker — who actually lives and hunts here.
          </Typography>
          <Stack spacing={1.25}>
            {kills.data.activeAlliances.length > 0 && (
              <Box>
                <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.7rem'), mb: 0.5 }}>Alliances</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {kills.data.activeAlliances.map((a) => (
                    <StatusBadge key={`a-${a.id}`} label={`${a.name} (${a.kills})`} accent={C.blue} />
                  ))}
                </Stack>
              </Box>
            )}
            {kills.data.activeAlliances.length > 0 && kills.data.activeCorps.length > 0 && <LcarsRule accent={C.blue} />}
            {kills.data.activeCorps.length > 0 && (
              <Box>
                <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.7rem'), mb: 0.5 }}>Corporations</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {kills.data.activeCorps.map((c) => (
                    <StatusBadge key={`c-${c.id}`} label={`${c.name} (${c.kills})`} accent={C.cyan} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </ConsolePanel>
      )}

      {kills.data && kills.data.kills.length > 0 && (
        <ConsolePanel
          title="Recent Kills"
          accent={C.red}
          corners={false}
          sx={frameSx(C.red)}
          action={
            <Stack direction="row" spacing={0.5}>
              {(['time', 'value'] as SortKey[]).map((s) => (
                <Chip
                  key={s}
                  label={s === 'time' ? 'Newest' : 'Highest value'}
                  size="small"
                  onClick={() => setSort(s)}
                  sx={{
                    bgcolor: 'transparent',
                    color: sort === s ? C.red : C.textDim,
                    border: `1px solid ${sort === s ? C.red : C.borderSub}`,
                    fontFamily: FONT.mono,
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Stack>
          }
        >
          <Stack spacing={1}>
            {sortedKills.map((k) => (
              <KillRow key={k.killmail_id} kill={k} />
            ))}
          </Stack>
        </ConsolePanel>
      )}
    </Stack>
  );
};

const HOUR_LABEL = (h: number) => `${String(h).padStart(2, '0')}:00`;

/** "Sat 14 Mar" — short, unambiguous, and stable regardless of the reader's
 * locale ordering, because the window can span a month boundary. */
function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * The window as an actual timeline: one column per UTC calendar day, tallest
 * = busiest. The weekday-average grid this replaces answered "which evenings
 * are usually bad", which is a different and less urgent question than "has
 * anyone died here THIS week" — and it hid a burst by smearing it across four
 * Tuesdays.
 *
 * Hovering (or tabbing to) a day drives a single readout line and reveals
 * that day's own 24-hour UTC strip. That is the delight moment and it is real
 * information: twelve kills spread across a day and twelve kills inside one
 * hour mean opposite things to a scout.
 */
const DayActivityChart: React.FC<{ days: DailyActivity[]; windowDays: number }> = ({ days, windowDays }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Defensive: a response cached before dailyActivity existed is undefined
  // here rather than an array, and must not blank the whole panel.
  if (!Array.isArray(days) || days.length === 0) return null;

  const max = Math.max(1, ...days.map((d) => d.total));
  const peakIndex = days.reduce((best, d, i) => (d.total > days[best].total ? i : best), 0);
  // The strip defaults to the busiest day rather than sitting empty: the slot
  // is always rendered anyway (so revealing it never reflows the panel), and
  // an empty one wastes it. Hovering replaces it; nothing is hidden.
  const active = activeIndex === null ? null : days[activeIndex];
  const stripDay = active ?? (days[peakIndex].total > 0 ? days[peakIndex] : null);
  const reduced = prefersReducedMotion();

  // Only ever a handful of ticks: the ends, plus the peak when it is not
  // crowding one of them. A label under all 30 columns is unreadable.
  const tickIndexes = new Set<number>([0, days.length - 1]);
  if (days[peakIndex].total > 0 && peakIndex > 1 && peakIndex < days.length - 2) tickIndexes.add(peakIndex);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 0.75 }}
      >
        <Typography sx={lcarsLabelSx(C.textDim, '0.74rem')}>Kills per day &mdash; {windowDays}d (UTC)</Typography>

        {/* One readout line rather than a floating tooltip: it never covers
            the bar you are pointing at, and it holds still long enough to
            actually read while you sweep across the window. */}
        <Typography
          aria-live="polite"
          sx={{
            fontFamily: FONT.mono,
            fontSize: '0.74rem',
            color: active ? C.cyan : C.textDim,
            transition: 'color 0.2s ease',
            minHeight: '1.1em',
          }}
        >
          {active
            ? `${formatDay(active.date)} — ${active.total} kill${active.total === 1 ? '' : 's'}${
                active.total > 0 ? ` · peak ${HOUR_LABEL(active.hours.indexOf(Math.max(...active.hours)))}` : ''
              }`
            : `busiest ${formatDay(days[peakIndex].date)} · ${days[peakIndex].total} kill${days[peakIndex].total === 1 ? '' : 's'}`}
        </Typography>
      </Stack>

      <Box
        role="group"
        aria-label={`Kills per day over the last ${windowDays} days`}
        onMouseLeave={() => setActiveIndex(null)}
        sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 84, mb: 0.5 }}
      >
        {days.map((day, i) => {
          const isActive = activeIndex === i;
          const heightPct = day.total === 0 ? 0 : Math.max(6, (day.total / max) * 100);
          return (
            <Box
              key={day.date}
              tabIndex={0}
              // Focus mirrors hover so the chart is fully readable by
              // keyboard — the readout above is the accessible output.
              onMouseEnter={() => setActiveIndex(i)}
              onFocus={() => setActiveIndex(i)}
              onBlur={() => setActiveIndex(null)}
              aria-label={`${formatDay(day.date)}: ${day.total} kill${day.total === 1 ? '' : 's'}`}
              sx={{
                flex: 1,
                minWidth: 3,
                height: '100%',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                cursor: 'default',
                outline: 'none',
                '&:focus-visible': { boxShadow: `0 0 0 1px ${C.cyan}`, borderRadius: '2px' },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  // Capped: at 1440px a 30-day window would otherwise give each
                  // column ~60px and read as a chunky bar chart rather than a
                  // timeline. Mobile is already below the cap.
                  maxWidth: 22,
                  // A zero day still draws a hairline: an empty column and a
                  // missing column must not look the same. Brighter than
                  // borderSub, which is too faint to register as a real datum.
                  height: day.total === 0 ? '2px' : `${heightPct}%`,
                  borderRadius: '2px',
                  bgcolor:
                    day.total === 0
                      ? hexToRgba(C.text, 0.14)
                      : hexToRgba(C.red, isActive ? 0.95 : 0.35 + 0.45 * (day.total / max)),
                  boxShadow: isActive && day.total > 0 ? `0 0 12px ${hexToRgba(C.red, 0.5)}` : 'none',
                  transformOrigin: 'bottom',
                  transition: 'background-color 0.18s ease, box-shadow 0.18s ease',
                  // The one authored entrance: the window draws itself in
                  // left to right, then holds still. Delay is capped so 30
                  // columns don't take three seconds to finish.
                  animation: reduced ? 'none' : `killDayRise 0.42s cubic-bezier(0.16,1,0.3,1) ${Math.min(i * 12, 240)}ms both`,
                  '@keyframes killDayRise': {
                    from: { transform: 'scaleY(0)', opacity: 0 },
                    to: { transform: 'scaleY(1)', opacity: 1 },
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: '2px', mb: 1.5 }}>
        {days.map((day, i) => (
          <Box key={day.date} sx={{ flex: 1, minWidth: 3, textAlign: 'center' }}>
            {tickIndexes.has(i) && (
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.55rem', color: C.textDim, whiteSpace: 'nowrap' }}>
                {new Date(`${day.date}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <HourStrip day={stripDay} isHovered={active !== null} />
    </Box>
  );
};

/**
 * The hovered day's own 24 UTC hours. Rendered in a fixed-height slot that is
 * always present, so revealing it never reflows the panel and nudges the bar
 * you are pointing at out from under the cursor.
 */
const HourStrip: React.FC<{ day: DailyActivity | null; isHovered: boolean }> = ({ day, isHovered }) => {
  const hours = day?.hours ?? [];
  const max = Math.max(1, ...hours);
  const hasData = !!day && day.total > 0;

  return (
    <Box sx={{ minHeight: 46, opacity: day ? 1 : 0.35, transition: 'opacity 0.2s ease' }}>
      <Typography sx={{ ...lcarsLabelSx(isHovered ? C.cyan : C.textDim, '0.66rem'), mb: 0.5, transition: 'color 0.2s ease' }}>
        {day
          ? `${formatDay(day.date)} — by hour (UTC)${isHovered ? '' : ' · busiest day'}`
          : 'Hover a day for its hourly breakdown'}
      </Typography>
      <Box sx={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: 22 }}>
        {Array.from({ length: 24 }).map((_, hour) => {
          const count = hours[hour] ?? 0;
          return (
            <Box
              key={hour}
              title={hasData ? `${HOUR_LABEL(hour)} UTC — ${count} kill${count === 1 ? '' : 's'}` : undefined}
              sx={{
                flex: 1,
                height: '100%',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                // Every third hour carries a faint baseline tick, so the
                // strip stays readable as a clock even on a silent day.
                borderBottom: hour % 3 === 0 ? `1px solid ${C.borderSub}` : 'none',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  // Same cap as the day bars: 24 cells across a wide panel
                  // would otherwise render one kill as a 77px slab.
                  maxWidth: 22,
                  // Full height scaled by transform rather than an animated
                  // `height`: the strip re-heights on every hover, and
                  // animating a layout property there thrashes layout on a
                  // 24-cell row. scaleY is composited.
                  height: '100%',
                  transform: `scaleY(${count === 0 ? 0.05 : Math.max(0.12, count / max)})`,
                  transformOrigin: 'bottom',
                  borderRadius: '1px',
                  bgcolor: count === 0 ? C.borderSub : hexToRgba(C.cyan, 0.4 + 0.6 * (count / max)),
                  transition: 'transform 0.2s cubic-bezier(0.16,1,0.3,1), background-color 0.2s ease',
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/** Cap on the calendar's mount stagger — same reasoning as the conduit draw
 * in SystemIntelPanel: past this the grid reads as loading, not opening,
 * however many weeks the window spans. */
const CAL_STAGGER_MS = 14;
const CAL_MAX_DELAY_MS = 260;
/* GitHub's contribution grid packs a whole YEAR into its row, which is why
 * its cells are ~11px. This window is 30-90 days — at most 14 columns — so
 * the same small cell left the instrument marooned in a corner of a wide
 * panel and gave a 18px tap target, under the 24px floor. Sized for the span
 * it actually shows. */
const CAL_CELL = 26;
const CAL_GAP = 5;
/** Square while the track is narrow; past CAL_CELL_MAX it stops growing
 * taller and just widens — a 7d window (2 weeks) would otherwise make each
 * cell a tile the size of the hour chart. */
const CAL_CELL_MAX = 48;
const CAL_CELL_SIZE_SX = { width: '100%', aspectRatio: '1', minHeight: CAL_CELL, maxHeight: CAL_CELL_MAX };

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
// Only these three rows get a visible label — GitHub's own contribution grid
// does the same; a letter on all seven rows at this cell size is noise.
const WEEKDAY_LABEL_ROWS = new Set([1, 3, 5]);

/** Slots the flat, oldest-first `dailyActivity` list into calendar weeks
 * (Sunday-first columns of 7), padding the first week with `null`s so every
 * day lands in its real weekday row — a GitHub-style contribution grid,
 * distinct from the flat timeline `DayActivityChart` already draws above
 * it. */
function buildCalendarWeeks(days: DailyActivity[]): (DailyActivity | null)[][] {
  if (days.length === 0) return [];
  const firstDow = new Date(`${days[0].date}T00:00:00Z`).getUTCDay();
  const cells: (DailyActivity | null)[] = [...Array(firstDow).fill(null), ...days];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (DailyActivity | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/**
 * Click-to-inspect companion to the bar timeline above: the same daily
 * totals laid out as a real Sunday-Saturday calendar grid — a wormhole
 * hole's activity read the way a contribution graph reads a year of
 * commits — brighter red = more kills that day.
 *
 * Paired with a weekday x hour punchcard rather than with a kill list: the
 * list was the same one Recent Kills renders in full below, so the panel
 * restated the page instead of adding to it. Selecting a day (click or
 * Enter/Space) now lifts that day's row in the punchcard beside it, which
 * makes the two halves one instrument — which days, and at what hours.
 * Defaults to the busiest day so it never opens inert.
 */
const KillCalendarSection: React.FC<{ days: DailyActivity[] }> = ({ days }) => {
  const weeks = useMemo(() => buildCalendarWeeks(days), [days]);
  const max = Math.max(1, ...days.map((d) => d.total));
  const peak = days.reduce((best, d) => (d.total > best.total ? d : best), days[0]);
  const [selected, setSelected] = useState<string | null>(peak.total > 0 ? peak.date : null);
  const reduced = prefersReducedMotion();

  let cellIndex = 0;

  return (
    <Box
      sx={{
        display: 'grid',
        // Both halves stretch: week columns flex to fill the calendar's
        // track, so it no longer sits marooned at the left edge.
        // minmax(0,...) rather than a bare 1fr, or the track floors at its
        // min-content and forces the panel wider than the page.
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: { xs: 2, md: 3 },
        alignItems: 'start',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: C.textDim, fontSize: '0.8rem', mb: 1.5 }}>
          Every watched day &mdash; click one for what happened on it.
        </Typography>

        {/* One grid, filled column by column: the weekday gutter, then a
            header + 7 cells per week. Week tracks take 1fr so the calendar
            spans its whole half of the panel; rows share the grid so the
            gutter letters stay level with the cells however tall they grow. */}
        <Box sx={{ overflowX: 'auto', ...thinScrollSx }}>
          <Box
            sx={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridTemplateRows: '14px repeat(7, auto)',
              gridTemplateColumns: `auto repeat(${weeks.length}, minmax(${CAL_CELL}px, 1fr))`,
              columnGap: `${CAL_GAP}px`,
              rowGap: `${CAL_GAP}px`,
              alignItems: 'center',
            }}
          >
            <Box />
            {WEEKDAY_LETTERS.map((l, i) => (
              <Typography key={i} sx={{ fontFamily: FONT.mono, fontSize: '0.6rem', color: C.textDim, pr: 0.75 }}>
                {WEEKDAY_LABEL_ROWS.has(i) ? l : ''}
              </Typography>
            ))}
            {weeks.map((week, wi) => (
              <React.Fragment key={wi}>
                {/* Month initial over the first week a new month starts in —
                    same sparse-tick philosophy as the bar chart's own
                    x-axis: only genuinely new information gets a label. */}
                <Box sx={{ height: 14 }}>
                  {week[0] && (wi === 0 || new Date(`${week[0].date}T00:00:00Z`).getUTCDate() <= 7) && (
                    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.6rem', color: C.textDim, whiteSpace: 'nowrap' }}>
                      {new Date(`${week[0].date}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })}
                    </Typography>
                  )}
                </Box>
                {week.map((day, di) => {
                  const delay = Math.min(cellIndex++ * CAL_STAGGER_MS, CAL_MAX_DELAY_MS);
                  return (
                    <CalendarCell
                      key={day?.date ?? `pad-${wi}-${di}`}
                      day={day}
                      max={max}
                      selected={!!day && day.date === selected}
                      delay={delay}
                      reduced={reduced}
                      onSelect={() => day && setSelected(day.date === selected ? null : day.date)}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <CalendarLegend />
        </Stack>
      </Box>

      {/* The recurring pattern, beside the calendar's individual days.
          
          This column used to hold the selected day's kill list — which is the
          same list Recent Kills renders in full further down the page, so the
          panel answered a question the page had already answered and made you
          read it twice.
          
          The hour profile answers what nothing else here does: not "which
          days were busy" (the calendar) and not "which hours on THAT day"
          (the Activity panel's own strip), but which hours are dangerous
          *recurringly* — the difference between a hole rolled once at noon
          and one hunted every EU evening. That decides when to rat. */}
      <Box
        sx={{
          minWidth: 0,
          pt: { xs: 2, md: 0 },
          pl: { xs: 0, md: 3 },
          borderTop: { xs: `1px solid ${hexToRgba(C.red, 0.28)}`, md: 'none' },
          borderLeft: { xs: 'none', md: `1px solid ${hexToRgba(C.red, 0.28)}` },
        }}
      >
        <HourProfile days={days} selectedDate={selected} reduced={reduced} />
      </Box>
    </Box>
  );
};

/** Sums the window into 24 UTC-hour buckets. */
function buildHourProfile(days: DailyActivity[]): number[] {
  const hours = Array(24).fill(0);
  for (const day of days) for (let h = 0; h < 24; h++) hours[h] += day.hours[h] ?? 0;
  return hours;
}

const WINDOW_HOURS = [0, 3, 6, 9, 12, 15, 18, 21];

/**
 * Every kill in the window folded onto one 24-hour clock.
 *
 * The calendar beside this is a TIMELINE — each date appears once, in order,
 * so a hole hunted every evening looks like a scatter of mild days and reads
 * the same as one loud afternoon. This is the FOLD: it collapses the window
 * so a recurring hour stacks up, which is the only way the page can answer
 * "what timezone hunts here" — the question that decides when it is safe to
 * rat, and one nothing else here answers.
 *
 * An earlier pass made this a weekday x hour punchcard. That is the textbook
 * shape for this job, and it was wrong here: 168 buckets fed by a 30-day
 * window is about four samples per weekday, so a single kill rendered as a
 * lone bright cell that looked like a pattern and was noise. Folding to 24
 * buckets puts every kill in the window into one, which is thin enough to
 * still be honest about and dense enough to actually read.
 */
const HourProfile: React.FC<{ days: DailyActivity[]; selectedDate: string | null; reduced: boolean }> = ({
  days,
  selectedDate,
  reduced,
}) => {
  const hours = useMemo(() => buildHourProfile(days), [days]);
  const max = Math.max(1, ...hours);
  const total = hours.reduce((a, b) => a + b, 0);
  const selectedDay = selectedDate ? days.find((d) => d.date === selectedDate) ?? null : null;

  // The busiest stretch, as a 3-hour block rather than a single spike: one
  // hour out of 24 is inside the noise even folded, three is a shift.
  const peakBlock = useMemo(() => {
    let best = { start: 0, count: -1 };
    for (let h = 0; h < 24; h++) {
      const count = hours[h] + hours[(h + 1) % 24] + hours[(h + 2) % 24];
      if (count > best.count) best = { start: h, count };
    }
    return best;
  }, [hours]);

  return (
    <Box>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.66rem'), mb: 0.25 }}>when it happens &middot; utc</Typography>
      <Typography sx={{ color: C.textDim, fontSize: '0.8rem', mb: 2, maxWidth: '68ch' }}>
        {total === 0
          ? 'Nothing died here in this window — no pattern to read yet.'
          : `Every kill in the window folded onto one clock. Busiest stretch: ${HOUR_LABEL(peakBlock.start)}–${HOUR_LABEL((peakBlock.start + 3) % 24)} UTC.`}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 110 }}>
        {hours.map((count, hour) => {
          // The selected day's own kills, drawn inside the window bar — so
          // clicking a date answers "was that day typical for this hole, or
          // was it the exception" without leaving the chart.
          const onSelectedDay = selectedDay?.hours[hour] ?? 0;
          return (
            <Tooltip
              key={hour}
              title={
                selectedDay
                  ? `${HOUR_LABEL(hour)} UTC — ${count} kill${count === 1 ? '' : 's'} across the window, ${onSelectedDay} on ${formatDay(selectedDay.date)}`
                  : `${HOUR_LABEL(hour)} UTC — ${count} kill${count === 1 ? '' : 's'} across the window`
              }
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  cursor: 'help',
                  '&:hover > *': { filter: 'brightness(1.35)' },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: `${Math.max(count === 0 ? 2 : 6, (count / max) * 100)}%`,
                    borderRadius: '2px 2px 0 0',
                    bgcolor: count === 0 ? hexToRgba(C.text, 0.08) : hexToRgba(C.red, 0.3 + 0.6 * (count / max)),
                    transformOrigin: 'bottom',
                    transition: 'filter 0.15s ease',
                    animation: reduced ? 'none' : `hourGrow 0.5s cubic-bezier(0.16,1,0.3,1) ${hour * 18}ms both`,
                    '@keyframes hourGrow': { from: { transform: 'scaleY(0)' }, to: { transform: 'scaleY(1)' } },
                  }}
                >
                  {onSelectedDay > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: `${(onSelectedDay / count) * 100}%`,
                        bgcolor: C.cyan,
                        borderRadius: '1px',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: '2px', mt: 0.5 }}>
        {hours.map((_, hour) => (
          <Box key={hour} sx={{ flex: 1, minWidth: 0 }}>
            {WINDOW_HOURS.includes(hour) && (
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.55rem', color: C.textDim, whiteSpace: 'nowrap' }}>
                {String(hour).padStart(2, '0')}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {selectedDay && (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: C.cyan, flexShrink: 0 }} />
          <Typography sx={{ color: C.textDim, fontSize: '0.74rem' }}>
            {formatDay(selectedDay.date)} &mdash; {selectedDay.total} of the window&rsquo;s {total}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const CalendarCell: React.FC<{
  day: DailyActivity | null;
  max: number;
  selected: boolean;
  delay: number;
  reduced: boolean;
  onSelect: () => void;
}> = ({ day, max, selected, delay, reduced, onSelect }) => {
  if (!day) return <Box aria-hidden sx={CAL_CELL_SIZE_SX} />;
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${formatDay(day.date)}: ${day.total} kill${day.total === 1 ? '' : 's'}`}
      title={`${formatDay(day.date)} — ${day.total} kill${day.total === 1 ? '' : 's'}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        ...CAL_CELL_SIZE_SX,
        borderRadius: '4px',
        cursor: 'pointer',
        outline: 'none',
        bgcolor: day.total === 0 ? hexToRgba(C.text, 0.1) : hexToRgba(C.red, 0.22 + 0.68 * (day.total / max)),
        boxShadow: selected ? `0 0 0 2px ${C.cyan}, 0 0 8px ${hexToRgba(C.cyan, 0.6)}` : 'none',
        transition: 'transform 0.12s ease, box-shadow 0.15s ease',
        animation: reduced ? 'none' : `calCellIn 0.3s cubic-bezier(0.16,1,0.3,1) ${delay}ms both`,
        '@keyframes calCellIn': { from: { opacity: 0, transform: 'scale(0.4)' }, to: { opacity: 1, transform: 'scale(1)' } },
        '&:hover': {
          transform: 'scale(1.2)',
          boxShadow: selected ? `0 0 0 2px ${C.cyan}, 0 0 10px ${hexToRgba(C.cyan, 0.7)}` : `0 0 6px ${hexToRgba(C.red, 0.5)}`,
        },
        '&:focus-visible': { boxShadow: `0 0 0 2px ${C.cyan}` },
      }}
    />
  );
};

const CalendarLegend: React.FC = () => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.6rem', color: C.textDim, mr: 0.25 }}>Less</Typography>
    {[0, 0.28, 0.5, 0.72, 0.95].map((a, i) => (
      <Box
        key={i}
        sx={{ width: 13, height: 13, borderRadius: '3px', bgcolor: a === 0 ? hexToRgba(C.text, 0.1) : hexToRgba(C.red, a) }}
      />
    ))}
    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.6rem', color: C.textDim, ml: 0.25 }}>More</Typography>
  </Stack>
);

export default SystemKillboard;
